/**
 * Animation definitions and timing functions
 */

// Easing functions
export const easings = {
  easeInOutQuad: t => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2,
  easeOutElastic: t => {
    const c4 = (2 * Math.PI) / 3;
    return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  },
  easeOutBounce: t => {
    const n1 = 7.5625;
    const d1 = 2.75;
    if (t < 1 / d1) {
      return n1 * t * t;
    } else if (t < 2 / d1) {
      return n1 * (t -= 1.5 / d1) * t + 0.75;
    } else if (t < 2.5 / d1) {
      return n1 * (t -= 2.25 / d1) * t + 0.9375;
    } else {
      return n1 * (t -= 2.625 / d1) * t + 0.984375;
    }
  },
  easeInOutCubic: t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  easeOutBack: t => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  }
};

// Animation step definitions with enhanced variety
// All animations return to starting position (rotations are multiples of 2π)
export const animationSteps = [
  {
    name: 'spin-y',
    duration: 1.2,
    easing: 'easeInOutCubic',
    transform: (t) => ({
      rotationY: t * Math.PI * 2,
      rotationX: 0,
      rotationZ: 0,
      offsetX: 0,
      offsetY: 0,
      scale: 1
    })
  },
  {
    name: 'spin-x',
    duration: 1.2,
    easing: 'easeInOutCubic',
    transform: (t) => ({
      rotationX: t * Math.PI * 2,
      rotationY: 0,
      rotationZ: 0,
      offsetX: 0,
      offsetY: 0,
      scale: 1
    })
  },
  {
    name: 'spiral',
    duration: 1.8,
    easing: 'easeInOutQuad',
    transform: (t) => ({
      rotationY: t * Math.PI * 4,
      rotationX: t * Math.PI * 2,
      rotationZ: 0,
      offsetX: 0,
      offsetY: 0,
      scale: 1
    })
  },
  {
    name: 'wobble',
    duration: 1.2,
    easing: 'easeInOutQuad',
    transform: (t) => ({
      rotationX: 0,
      rotationY: 0,
      rotationZ: Math.sin(t * Math.PI * 2) * 0.4,
      offsetX: Math.sin(t * Math.PI * 2) * 15,
      offsetY: 0,
      scale: 1
    })
  },
  {
    name: 'bounce-spin',
    duration: 1.4,
    easing: 'easeInOutQuad',
    transform: (t) => ({
      rotationY: t * Math.PI * 2,
      rotationX: 0,
      rotationZ: 0,
      offsetX: 0,
      offsetY: Math.sin(t * Math.PI * 2) * -30,
      scale: 1
    })
  },
  {
    name: 'figure-eight',
    duration: 2.0,
    easing: 'easeInOutQuad',
    transform: (t) => {
      const angle = t * Math.PI * 2;
      return {
        rotationX: Math.sin(angle) * 0.6,
        rotationY: t * Math.PI * 2,
        rotationZ: 0,
        offsetX: Math.sin(angle) * 20,
        offsetY: Math.sin(angle * 2) * 15,
        scale: 1
      };
    }
  },
  {
    name: 'tumble',
    duration: 1.6,
    easing: 'easeInOutCubic',
    transform: (t) => ({
      rotationX: t * Math.PI * 2,
      rotationY: t * Math.PI * 2,
      rotationZ: 0,
      offsetX: 0,
      offsetY: 0,
      scale: 1
    })
  },
  {
    name: 'pulse-spin',
    duration: 1.6,
    easing: 'easeInOutQuad',
    transform: (t) => {
      const pulse = 1 + Math.sin(t * Math.PI * 4) * 0.15;
      return {
        rotationY: t * Math.PI * 2,
        rotationX: 0,
        rotationZ: 0,
        offsetX: 0,
        offsetY: 0,
        scale: pulse
      };
    }
  },
  {
    name: 'shake-complex',
    duration: 1.2,
    easing: 'easeInOutQuad',
    transform: (t) => ({
      rotationX: Math.sin(t * Math.PI * 4) * 0.3,
      rotationY: Math.cos(t * Math.PI * 3) * 0.4,
      rotationZ: Math.sin(t * Math.PI * 5) * 0.2,
      offsetX: Math.sin(t * Math.PI * 4) * 12,
      offsetY: Math.cos(t * Math.PI * 4) * 12,
      scale: 1
    })
  },
  {
    name: 'carousel',
    duration: 2.5,
    easing: 'easeInOutQuad',
    transform: (t) => {
      const angle = t * Math.PI * 2;
      return {
        rotationX: Math.sin(angle) * 0.5,
        rotationY: t * Math.PI * 4,
        rotationZ: 0,
        offsetX: Math.sin(angle) * 25,
        offsetY: Math.cos(angle) * 15,
        scale: 1
      };
    }
  },
  {
    name: 'double-spin',
    duration: 1.8,
    easing: 'easeInOutCubic',
    transform: (t) => ({
      rotationX: t * Math.PI * 2,
      rotationY: t * Math.PI * 4,
      rotationZ: 0,
      offsetX: 0,
      offsetY: 0,
      scale: 1
    })
  },
  {
    name: 'wave',
    duration: 1.5,
    easing: 'easeInOutQuad',
    transform: (t) => ({
      rotationX: Math.sin(t * Math.PI * 2) * 0.8,
      rotationY: t * Math.PI * 2,
      rotationZ: Math.cos(t * Math.PI * 2) * 0.3,
      offsetX: 0,
      offsetY: 0,
      scale: 1
    })
  }
];

export class AnimationController {
  constructor(pauseDuration = 2000) {
    this.currentStep = 0;
    this.isAnimating = false;
    this.animationStartTime = 0;
    this.nextAnimationTimer = null;
    this.pauseDuration = pauseDuration;
    this.isHovered = false;
    this.oneTimeCallback = null;
    this.playingOneTime = false;
  }

  start() {
    if (!this.isAnimating && this.isHovered) {
      this.animationStartTime = performance.now();
      this.isAnimating = true;
    }
  }

  stop() {
    this.isAnimating = false;
    if (this.nextAnimationTimer) {
      clearTimeout(this.nextAnimationTimer);
      this.nextAnimationTimer = null;
    }
  }

  setHover(isHovered) {
    this.isHovered = isHovered;

    if (isHovered && !this.isAnimating) {
      setTimeout(() => {
        if (this.isHovered && !this.isAnimating) {
          this.start();
        }
      }, 150);
    } else if (!isHovered) {
      this.stop();
    }
  }

  playOneAnimation(callback) {
    // Play a single animation without hover
    this.playingOneTime = true;
    this.oneTimeCallback = callback;
    this.animationStartTime = performance.now();
    this.isAnimating = true;
    console.log('Starting one-time animation');
  }

  update() {
    if (!this.isAnimating) {
      return null;
    }

    const now = performance.now();
    const step = animationSteps[this.currentStep % animationSteps.length];
    const elapsed = (now - this.animationStartTime) / 1000;

    if (elapsed < step.duration) {
      const t = elapsed / step.duration;
      const easedT = easings[step.easing](t);
      return step.transform(easedT);
    } else {
      // Animation complete
      this.isAnimating = false;

      // If this was a one-time animation, call the callback and stop
      if (this.playingOneTime) {
        console.log('One-time animation complete, calling callback');
        this.playingOneTime = false;
        if (this.oneTimeCallback) {
          this.oneTimeCallback();
          this.oneTimeCallback = null;
        }
        return { rotationX: 0, rotationY: 0, rotationZ: 0, offsetX: 0, offsetY: 0, scale: 1 };
      }

      // Otherwise, advance to next step for hover mode
      this.currentStep++;
      if (this.currentStep >= animationSteps.length) {
        this.currentStep = 0;
      }

      if (this.isHovered) {
        this.nextAnimationTimer = setTimeout(() => {
          if (this.isHovered) {
            this.start();
          }
        }, this.pauseDuration);
      }

      return { rotationX: 0, rotationY: 0, rotationZ: 0, offsetX: 0, offsetY: 0, scale: 1 };
    }
  }

  dispose() {
    this.stop();
  }
}
